import { useEffect, useRef, useState } from "react";
import "./goals.scss";
import trasshIcon from "../../assets/media/icons/trashIcon.png";
import editIcon from "../../assets/media/icons/editIcon.png";

interface Goal {
  id: number;
  title: string;
  subtitle: string;
  details: string;
  ownerName: string;
  ownerImage: string;
  status: "progressing" | "finished" | "onhold";
}

const goalsData: Goal[] = [
  {
    id: 1,
    title: "Increase Sales Q3",
    subtitle: "Sales Target for Q3",
    details:
      "Aim to increase sales by 20% compared to last quarter. Focus on new customer acquisition and upselling.",
    ownerName: "Alice Johnson",
    ownerImage: "https://randomuser.me/api/portraits/women/44.jpg",
    status: "progressing",
  },
  {
    id: 2,
    title: "Improve Customer Support",
    subtitle: "Support Team Initiative",
    details:
      "Reduce average response time to under 2 hours and improve customer satisfaction scores.",
    ownerName: "Bob Smith",
    ownerImage: "https://randomuser.me/api/portraits/men/32.jpg",
    status: "onhold",
  },
  {
    id: 3,
    title: "Launch New Product",
    subtitle: "Product Development",
    details:
      "Complete development and launch of the new product by the end of Q4. Ensure all marketing materials are ready.",
    ownerName: "Charlie Brown",
    ownerImage: "https://randomuser.me/api/portraits/men/32.jpg",
    status: "progressing",
  },
  {
    id: 4,
    title: "Website Redesign",
    subtitle: "UX/UI Overhaul",
    details:
      "Redesign the company website to improve user experience and increase conversion rates. Target completion by end of Q2.",
    ownerName: "Diana Prince",
    ownerImage: "https://randomuser.me/api/portraits/men/32.jpg",
    status: "finished",
  },
];

const GoalsPage: React.FC = () => {
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editGoal, setEditGoal] = useState<Goal | null>(null);

  // Ref for dropdown
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeDropdown !== null) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setActiveDropdown(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [activeDropdown]);

  const handleEditClick = (goal: Goal) => {
    setEditGoal(goal);
    setShowEditPopup(true);
    setActiveDropdown(null); // Close dropdown
  };

  const handleCloseEditPopup = () => {
    setShowEditPopup(false);
    setEditGoal(null);
  };

  const handleUpdateGoal = () => {
    if (editGoal) {
      console.log("Updated Goal:", editGoal);
      setShowEditPopup(false);
      setEditGoal(null);
    }
  };

  return (
    <div className="goals-page">
      <span className="goals-header">Goals</span>
      <table className="goals-table">
        <thead>
          <tr>
            <th></th>
            <th>Owner</th>
            <th rowSpan={2}>Status</th>
          </tr>
        </thead>
        <tbody>
          {goalsData.map((goal) => (
            <tr key={goal.id}>
              <td>
                <div className="goal-title">{goal.title}</div>
                <div className="goal-subtitle">{goal.subtitle}</div>
                <div className="goal-details">{goal.details}</div>
              </td>
              <td>
                <div className="owner-info">
                  <img
                    src={goal.ownerImage}
                    alt={goal.ownerName}
                    className="owner-image"
                  />
                  <span className="owner-name">{goal.ownerName}</span>
                </div>
              </td>
              <td>
                <span className={`goal-status ${goal.status}`}>
                  {goal.status === "progressing"
                    ? "Progressing"
                    : goal.status === "onhold"
                    ? "On Hold"
                    : "Finished"}
                </span>
              </td>
              <td style={{ position: "relative" }}>
                <div
                  className="vertical-ellipsis"
                  onClick={() => setActiveDropdown(goal.id)}
                  tabIndex={0}
                >
                  <span className="vertical-ellipsis-dot"></span>
                  <span className="vertical-ellipsis-dot"></span>
                  <span className="vertical-ellipsis-dot"></span>
                </div>
                {activeDropdown === goal.id && (
                  <div className="goal-dropdown-menu" ref={dropdownRef}>
                    <div
                      className="goal-dropdown-item"
                      onClick={() => handleEditClick(goal)}
                    >
                      <span className="goal-dropdown-icon">
                        <img src={editIcon} alt="Edit" className="goal-icon" />
                      </span>
                      Edit
                    </div>
                    <div className="goal-dropdown-item">
                      <span className="goal-dropdown-icon">
                        <img
                          src={trasshIcon}
                          alt="Delete"
                          className="goal-icon"
                        />
                      </span>
                      Delete
                    </div>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showEditPopup && editGoal && (
        <div className="edit-goal-overlay">
          <div className="edit-goal-popup">
            <div className="edit-goal-header">
              <span>Edit Goal</span>
              <button
                className="edit-goal-close"
                onClick={handleCloseEditPopup}
              >
                &times;
              </button>
            </div>
            <div className="edit-goal-body">
              <label>Goal Name</label>
              <input
                type="text"
                defaultValue={editGoal.title}
                className="edit-goal-input"
              />
              <label>Description</label>
              <textarea
                defaultValue={editGoal.details}
                className="edit-goal-textarea"
                rows={3}
              />
              <div className="edit-goal-owner-label">Owner</div>
              <div className="edit-goal-owner">
                <img
                  src={editGoal.ownerImage}
                  alt={editGoal.ownerName}
                  className="edit-goal-owner-image"
                />
                <span className="edit-goal-owner-name">
                  {editGoal.ownerName}
                </span>
              </div>
              <label>Status</label>
              <select
                defaultValue={editGoal.status}
                className="edit-goal-select"
              >
                <option value="progressing">Progressing</option>
                <option value="finished">Finished</option>
                <option value="onhold">On Hold</option>
              </select>
            </div>
            <div className="edit-goal-actions">
              <button className="edit-goal-update" onClick={handleUpdateGoal}>
                Update Goal
              </button>
              <button
                className="edit-goal-cancel"
                onClick={handleCloseEditPopup}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoalsPage;
